import { RefreshCwIcon, CloudIcon, MapIcon, DatabaseIcon, TrendingUpIcon } from 'lucide-react';
import { Card, CardHeader, CardContent, StatusBadge } from '@etherisc/ui-kit';
import { PrimaryButton } from '../buttons';
import type { CoverageSummary, RegionalCoverageSummary } from '../../types/coverage';

interface CoverageSummaryViewProps {
  summaryData: CoverageSummary | null;
  regionalSummaries: RegionalCoverageSummary[];
  loading?: boolean;
  onRefresh?: () => void;
}

export function CoverageSummaryView({
  summaryData,
  regionalSummaries,
  loading = false,
  onRefresh
}: CoverageSummaryViewProps) {
  // Return loading state if no data yet
  if (!summaryData || loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1>Coverage Management</h1>
          <p>Loading coverage summary...</p>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-gray-50">
              <CardHeader>
                <h3>Loading...</h3>
              </CardHeader>
              <CardContent>
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Stats data following dashboard pattern exactly
  const stats = [
    {
      label: "Total Coverage",
      value: summaryData.total_coverage.toString(),
      change: `${summaryData.active_coverage} active`,
      status: "success" as const,
      icon: <CloudIcon size={20} />,
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      label: "Active Coverage", 
      value: summaryData.active_coverage.toString(),
      change: `${Math.round((summaryData.active_coverage / summaryData.total_coverage) * 100)}% of total`,
      status: "success" as const,
      icon: <TrendingUpIcon size={20} />,
      bgColor: "bg-green-50",
      iconColor: "text-green-600",
    },
    {
      label: "Pending Coverage",
      value: summaryData.pending_coverage.toString(), 
      change: "Awaiting validation",
      status: "warning" as const,
      icon: <DatabaseIcon size={20} />,
      bgColor: "bg-orange-50",
      iconColor: "text-orange-600",
    },
    {
      label: "Regional Data",
      value: summaryData.by_region.length.toString(),
      change: "Coverage regions",
      status: "info" as const,
      icon: <MapIcon size={20} />,
      bgColor: "bg-purple-50",
      iconColor: "text-purple-600",
    },
  ];



  return (
    <div className="space-y-6">
      {/* Page Heading - exactly like dashboard */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1>Coverage Management</h1>
            <p>
              Monitor and manage climate data coverage across all regions and data sources.
            </p>
          </div>
          {onRefresh && (
            <PrimaryButton onClick={onRefresh} size="sm">
              <RefreshCwIcon size={16} />
              Refresh
            </PrimaryButton>
          )}
        </div>
      </div>

      {/* Stats Cards - exactly like dashboard pattern */}
      <div className="grid grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className={stat.bgColor}>
            <CardHeader>
              <h3>{stat.label}</h3>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex flex-col items-start">
                  <p className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</p>
                  <p className="text-lg font-medium text-gray-700">{stat.change}</p>
                </div>
                <div className={`w-8 h-8 bg-white ${stat.iconColor} rounded-full flex items-center justify-center shadow-sm`}>
                  {stat.icon}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Regional Summaries - using Card pattern */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <MapIcon size={24} />
            <div>
              <h3>Regional Coverage Summary</h3>
              <p className="text-sm text-gray-600">Coverage distribution across geographical regions</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {regionalSummaries.map((region) => (
              <div key={region.regionId} className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-lg">{region.regionName}</h4>
                    <StatusBadge variant={region.dataQuality === 'excellent' ? 'success' : region.dataQuality === 'good' ? 'info' : 'warning'}>
                      {region.dataQuality}
                    </StatusBadge>
                  </div>
                  <p className="text-sm text-gray-600">
                    {region.activeCoverage} of {region.totalCoverage} coverage items active
                  </p>
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm font-medium">Coverage by Type:</p>
                  {region.coverageByType.map((type) => (
                    <div key={type.type} className="flex items-center justify-between text-sm">
                      <span className="capitalize">{type.type.replace('_', ' ')}</span>
                      <span className="font-medium">{type.count} items ({type.percentage.toFixed(1)}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Data Source & Type Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h3>Data Sources</h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {summaryData.by_data_source.map((source) => (
                <div key={source.data_source} className="flex items-center justify-between">
                  <span className="font-medium">{source.data_source}</span>
                  <span className="text-sm text-gray-600">{source.count} items</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3>Coverage Types</h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {summaryData.by_coverage_type.map((type) => (
                <div key={type.coverage_type} className="flex items-center justify-between">
                  <span className="font-medium capitalize">{type.coverage_type.replace('_', ' ')}</span>
                  <span className="text-sm text-gray-600">{type.count} items</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 